import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Save, Trash2, X } from 'lucide-react';
import {
  CUSTOM_FIELD_TYPES,
  type CreateCustomFieldDefinitionBody,
  type CustomFieldDefinition,
  type CustomFieldOption,
  type CustomFieldType,
} from '@molt/shared';

import { Button, Dialog, IconButton, Notice, TextField } from '@/components';
import { cn } from '@/lib/cn';
import { customFieldTypeLabel } from '@/lib/custom-field-type';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface BoardCustomFieldDefinitionPositionPatch {
  previousDefinitionId: string | null;
  nextDefinitionId: string | null;
}

export interface BoardCustomFieldOptionPositionPatch {
  previousOptionId: string | null;
  nextOptionId: string | null;
}

export interface BoardCustomFieldsSectionProps {
  definitions?: readonly CustomFieldDefinition[] | undefined;
  canManage: boolean;
  disabledReason: string | null;
  loading?: boolean | undefined;
  createBusy?: boolean | undefined;
  busyDefinitionIds?: readonly string[] | undefined;
  busyOptionIds?: readonly string[] | undefined;
  error?: string | undefined;
  onCreateDefinition: (body: CreateCustomFieldDefinitionBody) => void;
  onRenameDefinition: (definitionId: string, name: string) => void;
  onToggleHidden: (definitionId: string, hidden: boolean) => void;
  onToggleShowOnFront: (definitionId: string, showOnFront: boolean) => void;
  onDeleteDefinition: (definitionId: string) => void;
  onReorderDefinition: (
    definitionId: string,
    patch: BoardCustomFieldDefinitionPositionPatch,
  ) => void;
  onAddOption: (definitionId: string, label: string) => void;
  onRenameOption: (optionId: string, label: string) => void;
  onDeleteOption: (optionId: string) => void;
  onReorderOption: (optionId: string, patch: BoardCustomFieldOptionPositionPatch) => void;
  className?: string | undefined;
}

/* FE-80.1 — the `TYPE_LABELS` record that stood here is GONE, not routed in
   place. It was byte-identical to `card-back/CardCustomFieldsSection.tsx`'s
   `fieldTypeLabel`, and the card-custom-fields return named the third author of
   those five words as the point at which the duplication becomes a leaf. Both
   frames now call `customFieldTypeLabel` (`lib/custom-field-type.ts`), which
   reads `primitives.customFieldType.*`. Routing this copy separately would have
   frozen the divergence rather than closed it. */

function parseDropdownOptions(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((option) => option.trim())
    .filter((option) => option.length > 0);
}

function definitionPositionPatch(
  definitions: readonly CustomFieldDefinition[],
  index: number,
  direction: 'up' | 'down',
): BoardCustomFieldDefinitionPositionPatch {
  if (direction === 'up') {
    return {
      previousDefinitionId: definitions[index - 2]?.id ?? null,
      nextDefinitionId: definitions[index - 1]?.id ?? null,
    };
  }
  return {
    previousDefinitionId: definitions[index + 1]?.id ?? null,
    nextDefinitionId: definitions[index + 2]?.id ?? null,
  };
}

function optionPositionPatch(
  options: readonly CustomFieldOption[],
  index: number,
  direction: 'up' | 'down',
): BoardCustomFieldOptionPositionPatch {
  if (direction === 'up') {
    return {
      previousOptionId: options[index - 2]?.id ?? null,
      nextOptionId: options[index - 1]?.id ?? null,
    };
  }
  return {
    previousOptionId: options[index + 1]?.id ?? null,
    nextOptionId: options[index + 2]?.id ?? null,
  };
}

/**
 * ⚠ The glyphs are ROUTED, not treated as language-neutral. Three of the five
 * are English words rather than symbols — `Aa` is the Latin alphabet, `Yes` is
 * an English word, `List` is another — and a locale that changes
 * `primitives.customFieldType.checkbox` and cannot change the two-letter badge
 * beside it renders a mixed row. `123` and `31` translate to themselves in
 * every locale this catalogue can serve today and cost one key each to keep the
 * five together.
 */
function FieldTypeIcon({
  t,
  type,
}: {
  t: InterfaceLanguageContextValue['t'];
  type: CustomFieldType;
}): ReactElement {
  const glyph: Record<CustomFieldType, string> = {
    text: t('boardCustomFields.typeGlyph.text'),
    number: t('boardCustomFields.typeGlyph.number'),
    checkbox: t('boardCustomFields.typeGlyph.checkbox'),
    date: t('boardCustomFields.typeGlyph.date'),
    dropdown: t('boardCustomFields.typeGlyph.dropdown'),
  };

  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-card bg-surface text-xs font-semibold text-ink-muted">
      {glyph[type]}
    </span>
  );
}

function reasonForControls(
  t: InterfaceLanguageContextValue['t'],
  canManage: boolean,
  disabledReason: string | null,
  loading: boolean,
): string | undefined {
  if (loading) return t('boardCustomFields.loading');
  if (!canManage)
    // `disabledReason` is a PROP — a caller's own sentence, already routed or
    // not by whoever owns that caller. Only the fallback is this file's to say.
    return disabledReason ?? t('boardCustomFields.noPermission');
  return undefined;
}

/**
 * The four whole sentences a definition's meta line can be, rather than a type
 * noun with two English fragments concatenated onto it. The suffix shape
 * (`' - Hidden'`, `' - Shows on card front'`) cannot be reordered, cannot drop
 * the separator, and needs a leading space inside a translated value — the
 * three things this catalogue has now paid for separately.
 */
function definitionMeta(
  t: InterfaceLanguageContextValue['t'],
  definition: CustomFieldDefinition,
): string {
  const type = customFieldTypeLabel(t, definition.type);
  if (definition.hidden && definition.showOnFront)
    return t('boardCustomFields.meta.hiddenAndShowsOnFront', { type });
  if (definition.hidden) return t('boardCustomFields.meta.hidden', { type });
  if (definition.showOnFront) return t('boardCustomFields.meta.showsOnFront', { type });
  return t('boardCustomFields.meta.plain', { type });
}

function iconClassName(): string {
  return 'size-4';
}

export function BoardCustomFieldsSection({
  definitions,
  canManage,
  disabledReason,
  loading = false,
  createBusy = false,
  busyDefinitionIds = [],
  busyOptionIds = [],
  error,
  onCreateDefinition,
  onRenameDefinition,
  onToggleHidden,
  onToggleShowOnFront,
  onDeleteDefinition,
  onReorderDefinition,
  onAddOption,
  onRenameOption,
  onDeleteOption,
  onReorderOption,
  className,
}: BoardCustomFieldsSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [createName, setCreateName] = useState('');
  const [createType, setCreateType] = useState<CustomFieldType>('text');
  const [createOptions, setCreateOptions] = useState('');
  const [definitionNames, setDefinitionNames] = useState<Record<string, string>>({});
  const [optionLabels, setOptionLabels] = useState<Record<string, string>>({});
  const [newOptions, setNewOptions] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<CustomFieldDefinition | null>(null);
  const selectId = useId();

  const fields = definitions ?? [];
  const controlReason = reasonForControls(t, canManage, disabledReason, loading);
  const controlsDisabled = controlReason !== undefined;
  const createDisabled = controlsDisabled || createName.trim().length === 0;

  function submitCreate(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const name = createName.trim();
    if (name.length === 0 || controlsDisabled) return;

    if (createType === 'dropdown') {
      onCreateDefinition({
        type: 'dropdown',
        name,
        hidden: false,
        showOnFront: false,
        options: parseDropdownOptions(createOptions),
      });
    } else {
      onCreateDefinition({
        type: createType,
        name,
        hidden: false,
        showOnFront: false,
      });
    }

    setCreateName('');
    setCreateOptions('');
  }

  function submitDefinitionName(definition: CustomFieldDefinition): void {
    const nextName = (definitionNames[definition.id] ?? definition.name).trim();
    if (nextName.length === 0 || nextName === definition.name || controlsDisabled) return;
    onRenameDefinition(definition.id, nextName);
    setDefinitionNames(({ [definition.id]: _removed, ...rest }) => rest);
  }

  function submitNewOption(definition: CustomFieldDefinition): void {
    const label = (newOptions[definition.id] ?? '').trim();
    if (label.length === 0 || controlsDisabled) return;
    onAddOption(definition.id, label);
    setNewOptions(({ [definition.id]: _removed, ...rest }) => rest);
  }

  function submitOptionLabel(option: CustomFieldOption): void {
    const label = (optionLabels[option.id] ?? option.label).trim();
    if (label.length === 0 || label === option.label || controlsDisabled) return;
    onRenameOption(option.id, label);
    setOptionLabels(({ [option.id]: _removed, ...rest }) => rest);
  }

  function definitionBusy(definition: CustomFieldDefinition): boolean {
    return busyDefinitionIds.includes(definition.id);
  }

  function optionBusy(option: CustomFieldOption): boolean {
    return busyOptionIds.includes(option.id);
  }

  return (
    <section
      className={cn('flex flex-col gap-4', className)}
      aria-label={t('boardCustomFields.heading')}
    >
      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
      {loading ? (
        <Notice tone="info" body={t('boardCustomFields.loadingNotice')} />
      ) : null}
      {!loading && !canManage && controlReason !== undefined ? (
        <Notice tone="locked" body={controlReason} />
      ) : null}

      <form
        className="flex flex-col gap-3 rounded-card border border-border-strong bg-surface-raised p-3"
        onSubmit={submitCreate}
      >
        <h3 className="text-sm font-semibold text-ink">
          {t('boardCustomFields.createHeading')}
        </h3>
        <TextField
          label={t('boardCustomFields.nameLabel')}
          value={createName}
          disabled={controlsDisabled}
          onChange={(event) => setCreateName(event.currentTarget.value)}
        />
        <label
          className="flex flex-col gap-1 text-sm font-medium text-ink"
          htmlFor={selectId}
        >
          {t('boardCustomFields.typeLabel')}
          <select
            id={selectId}
            className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
            value={createType}
            disabled={controlsDisabled}
            onChange={(event) =>
              setCreateType(event.currentTarget.value as CustomFieldType)
            }
          >
            {CUSTOM_FIELD_TYPES.map((type) => (
              <option key={type} value={type}>
                {customFieldTypeLabel(t, type)}
              </option>
            ))}
          </select>
        </label>
        {createType === 'dropdown' ? (
          <TextField
            label={t('boardCustomFields.dropdownOptionsLabel')}
            multiline
            value={createOptions}
            disabled={controlsDisabled}
            hint={t('boardCustomFields.dropdownOptionsHint')}
            onChange={(event) => setCreateOptions(event.currentTarget.value)}
          />
        ) : null}
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            busy={createBusy}
            iconStart={<Plus className={iconClassName()} />}
            disabled={createDisabled}
            disabledReason={createDisabled ? controlReason : undefined}
          >
            {t('boardCustomFields.createField')}
          </Button>
        </div>
      </form>

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-ink">
          {t('boardCustomFields.definitionsHeading')}
        </h3>
        {fields.length === 0 ? (
          <p className="rounded-card bg-surface-raised p-3 text-sm text-ink-muted">
            {t('boardCustomFields.empty')}
          </p>
        ) : (
          fields.map((definition, index) => (
            <article
              key={definition.id}
              className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-3"
              aria-labelledby={`custom-field-${definition.id}-title`}
            >
              <div className="flex items-start gap-3">
                <FieldTypeIcon t={t} type={definition.type} />
                <div className="min-w-0 flex-1">
                  <h4
                    id={`custom-field-${definition.id}-title`}
                    className="truncate text-sm font-semibold text-ink"
                  >
                    {definition.name}
                  </h4>
                  <p className="text-xs text-ink-muted">
                    {definitionMeta(t, definition)}
                  </p>
                </div>
              </div>

              <form
                className="flex flex-col gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  submitDefinitionName(definition);
                }}
              >
                <TextField
                  label={t('boardCustomFields.renameLabel', { name: definition.name })}
                  value={definitionNames[definition.id] ?? definition.name}
                  disabled={controlsDisabled || definitionBusy(definition)}
                  onChange={(event) =>
                    setDefinitionNames((current) => ({
                      ...current,
                      [definition.id]: event.currentTarget.value,
                    }))
                  }
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    iconStart={<Save className={iconClassName()} />}
                    busy={definitionBusy(definition)}
                    disabled={controlsDisabled}
                    disabledReason={controlReason}
                  >
                    {t('boardCustomFields.saveName')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="toggle"
                    pressed={definition.hidden}
                    iconStart={
                      definition.hidden ? (
                        <EyeOff className={iconClassName()} />
                      ) : (
                        <Eye className={iconClassName()} />
                      )
                    }
                    busy={definitionBusy(definition)}
                    disabled={controlsDisabled}
                    disabledReason={controlReason}
                    onClick={() => onToggleHidden(definition.id, !definition.hidden)}
                  >
                    {definition.hidden
                      ? t('boardCustomFields.unhide')
                      : t('boardCustomFields.hide')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="toggle"
                    pressed={definition.showOnFront}
                    busy={definitionBusy(definition)}
                    disabled={controlsDisabled}
                    disabledReason={controlReason}
                    onClick={() =>
                      onToggleShowOnFront(definition.id, !definition.showOnFront)
                    }
                  >
                    {t('boardCustomFields.showOnFront')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="quiet"
                    aria-label={t('boardCustomFields.moveUp', { name: definition.name })}
                    iconStart={<ArrowUp className={iconClassName()} />}
                    busy={definitionBusy(definition)}
                    disabled={controlsDisabled || index === 0}
                    disabledReason={
                      controlsDisabled
                        ? controlReason
                        : t('boardCustomFields.alreadyFirst')
                    }
                    onClick={() =>
                      onReorderDefinition(
                        definition.id,
                        definitionPositionPatch(fields, index, 'up'),
                      )
                    }
                  >
                    {t('boardCustomFields.up')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="quiet"
                    aria-label={t('boardCustomFields.moveDown', {
                      name: definition.name,
                    })}
                    iconStart={<ArrowDown className={iconClassName()} />}
                    busy={definitionBusy(definition)}
                    disabled={controlsDisabled || index === fields.length - 1}
                    disabledReason={
                      controlsDisabled
                        ? controlReason
                        : t('boardCustomFields.alreadyLast')
                    }
                    onClick={() =>
                      onReorderDefinition(
                        definition.id,
                        definitionPositionPatch(fields, index, 'down'),
                      )
                    }
                  >
                    {t('boardCustomFields.down')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    iconStart={<Trash2 className={iconClassName()} />}
                    busy={definitionBusy(definition)}
                    disabled={controlsDisabled}
                    disabledReason={controlReason}
                    onClick={() => setDeleteTarget(definition)}
                  >
                    {t('boardCustomFields.delete')}
                  </Button>
                </div>
              </form>

              {definition.type === 'dropdown' ? (
                <DropdownOptionsEditor
                  definition={definition}
                  controlsDisabled={controlsDisabled}
                  controlReason={controlReason}
                  newOptionValue={newOptions[definition.id] ?? ''}
                  optionLabels={optionLabels}
                  optionBusy={optionBusy}
                  onNewOptionValueChange={(value) =>
                    setNewOptions((current) => ({ ...current, [definition.id]: value }))
                  }
                  onSubmitNewOption={() => submitNewOption(definition)}
                  onOptionLabelChange={(optionId, value) =>
                    setOptionLabels((current) => ({ ...current, [optionId]: value }))
                  }
                  onSubmitOptionLabel={submitOptionLabel}
                  onDeleteOption={onDeleteOption}
                  onReorderOption={onReorderOption}
                />
              ) : null}
            </article>
          ))
        )}
      </div>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('boardCustomFields.deleteTitle')}
        description={
          deleteTarget === null
            ? undefined
            : t('boardCustomFields.deleteDescription', { name: deleteTarget.name })
        }
        closeControl={
          <IconButton
            label={t('boardCustomFields.deleteClose')}
            size="sm"
            variant="quiet"
          >
            <X className={iconClassName()} />
          </IconButton>
        }
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
            >
              {t('boardCustomFields.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              iconStart={<Trash2 className={iconClassName()} />}
              onClick={() => {
                if (deleteTarget === null) return;
                onDeleteDefinition(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              {t('boardCustomFields.deleteField')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink">{t('boardCustomFields.deleteBody')}</p>
      </Dialog>
    </section>
  );
}

function DropdownOptionsEditor({
  definition,
  controlsDisabled,
  controlReason,
  newOptionValue,
  optionLabels,
  optionBusy,
  onNewOptionValueChange,
  onSubmitNewOption,
  onOptionLabelChange,
  onSubmitOptionLabel,
  onDeleteOption,
  onReorderOption,
}: {
  definition: CustomFieldDefinition;
  controlsDisabled: boolean;
  controlReason: string | undefined;
  newOptionValue: string;
  optionLabels: Record<string, string>;
  optionBusy: (option: CustomFieldOption) => boolean;
  onNewOptionValueChange: (value: string) => void;
  onSubmitNewOption: () => void;
  onOptionLabelChange: (optionId: string, value: string) => void;
  onSubmitOptionLabel: (option: CustomFieldOption) => void;
  onDeleteOption: (optionId: string) => void;
  onReorderOption: (optionId: string, patch: BoardCustomFieldOptionPositionPatch) => void;
}): ReactElement {
  // Its own `useInterfaceLanguage()` rather than a `t` prop from the parent:
  // the hook is the house idiom, and one more prop on a fourteen-prop
  // component is a prop that crosses two files for no gain.
  const { t } = useInterfaceLanguage();

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      <h5 className="text-sm font-semibold text-ink">
        {t('boardCustomFields.options.heading')}
      </h5>
      {definition.options.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('boardCustomFields.options.empty')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {definition.options.map((option, index) => (
            <form
              key={option.id}
              className="flex flex-col gap-2 rounded-card bg-surface p-2"
              onSubmit={(event) => {
                event.preventDefault();
                onSubmitOptionLabel(option);
              }}
            >
              <TextField
                label={t('boardCustomFields.options.renameLabel', {
                  label: option.label,
                })}
                value={optionLabels[option.id] ?? option.label}
                disabled={controlsDisabled || optionBusy(option)}
                onChange={(event) =>
                  onOptionLabelChange(option.id, event.currentTarget.value)
                }
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  size="sm"
                  iconStart={<Save className={iconClassName()} />}
                  busy={optionBusy(option)}
                  disabled={controlsDisabled}
                  disabledReason={controlReason}
                >
                  {t('boardCustomFields.options.save')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  aria-label={t('boardCustomFields.options.moveUp', {
                    label: option.label,
                  })}
                  iconStart={<ArrowUp className={iconClassName()} />}
                  busy={optionBusy(option)}
                  disabled={controlsDisabled || index === 0}
                  disabledReason={
                    controlsDisabled
                      ? controlReason
                      : t('boardCustomFields.options.alreadyFirst')
                  }
                  onClick={() =>
                    onReorderOption(
                      option.id,
                      optionPositionPatch(definition.options, index, 'up'),
                    )
                  }
                >
                  {t('boardCustomFields.options.up')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  aria-label={t('boardCustomFields.options.moveDown', {
                    label: option.label,
                  })}
                  iconStart={<ArrowDown className={iconClassName()} />}
                  busy={optionBusy(option)}
                  disabled={controlsDisabled || index === definition.options.length - 1}
                  disabledReason={
                    controlsDisabled
                      ? controlReason
                      : t('boardCustomFields.options.alreadyLast')
                  }
                  onClick={() =>
                    onReorderOption(
                      option.id,
                      optionPositionPatch(definition.options, index, 'down'),
                    )
                  }
                >
                  {t('boardCustomFields.options.down')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  iconStart={<Trash2 className={iconClassName()} />}
                  busy={optionBusy(option)}
                  disabled={controlsDisabled}
                  disabledReason={controlReason}
                  onClick={() => onDeleteOption(option.id)}
                >
                  {t('boardCustomFields.options.delete')}
                </Button>
              </div>
            </form>
          ))}
        </div>
      )}
      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmitNewOption();
        }}
      >
        <TextField
          label={t('boardCustomFields.options.newLabel', { name: definition.name })}
          value={newOptionValue}
          disabled={controlsDisabled}
          onChange={(event) => onNewOptionValueChange(event.currentTarget.value)}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            iconStart={<Plus className={iconClassName()} />}
            disabled={controlsDisabled || newOptionValue.trim().length === 0}
            disabledReason={controlReason}
          >
            {t('boardCustomFields.options.add')}
          </Button>
        </div>
      </form>
    </div>
  );
}
